import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { Stack, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { ICON_SIZE } from "../../../theme/tokens";
import type { PbjPlanActionProgress } from "../../../utils/models";
import { paceCopy } from "./planActionCopy.utils";
import type { ActionFormat } from "./planValue.utils";

type Props = {
	action: PbjPlanActionProgress;
	format: ActionFormat;
	weeksRemaining: number;
	/** Band edges that lie past the commitment, so are not drawn on the bar. */
	beyondPlan: number;
	/** The lever moved against its commitment — see `movedBackwards`. */
	backwards: boolean;
};

/** The lines under an action's bar: the projection in words, and the notes that qualify it. */
export const PlanActionFootnotes: FC<Props> = ({ action, format, weeksRemaining, beyondPlan, backwards }) => {
	const { t } = useTranslation();
	const isCut = action.direction === "cut";
	const pace = action.projected ? paceCopy(action, action.projected, format) : null;
	// The one withheld-forecast case worth explaining: a measurable lever that has had no day it can be
	// worked on yet, which is a weekend one before its first weekend. It resolves by itself within days,
	// so the reader is promised something. An INFEASIBLE lever is deliberately excluded — its own chip
	// already says it cannot be measured at all, and nothing is coming.
	const projectionPending = !action.projected && action.feasible && action.days.elapsed === 0;
	const quarterComplete = action.feasible && weeksRemaining === 0;

	return (
		<>
			{/* The projection in words, because the marker alone cannot say what the landing spot is worth. */}
			{pace && (
				<Stack direction="row" spacing={0.75} sx={{ alignItems: "flex-start", mt: 1 }} data-testid={`pbj-model-plan-action-pace-${action.id}`}>
					<TrendingUpIcon sx={{ fontSize: ICON_SIZE.PILL, color: "text.disabled", mt: 0.25, flexShrink: 0 }} />
					<Typography variant="caption" sx={{ color: "text.secondary" }}>
						{t(pace.key, pace.values)}
					</Typography>
				</Stack>
			)}
			{/* The forecast is withheld through week 0 rather than extrapolated off a single day. Saying so
			    beats letting the marker simply not be there: its absence is indistinguishable from a bug. */}
			{projectionPending && (
				<Typography
					variant="caption"
					sx={{ display: "block", fontStyle: "italic", mt: 0.75, color: "text.secondary" }}
					data-testid={`pbj-model-plan-action-pace-pending-${action.id}`}
				>
					{t("pbj_model.tracker.projection_pending_note")}
				</Typography>
			)}
			{(beyondPlan > 0 || quarterComplete || backwards) && (
				<Stack spacing={0.25} sx={{ mt: 0.75 }}>
					{backwards && (
						<Typography variant="caption" sx={{ fontStyle: "italic", color: "error.main" }} data-testid={`pbj-model-plan-action-backwards-${action.id}`}>
							{t(isCut ? "pbj_model.tracker.above_baseline_note" : "pbj_model.tracker.below_baseline_note")}
						</Typography>
					)}
					{beyondPlan > 0 && (
						<Typography variant="caption" sx={{ fontStyle: "italic", color: "text.secondary" }}>
							{t(isCut ? "pbj_model.tracker.milestones_beyond_plan_cut" : "pbj_model.tracker.milestones_beyond_plan", { count: beyondPlan })}
						</Typography>
					)}
					{quarterComplete && (
						<Typography variant="caption" sx={{ fontStyle: "italic", color: "text.secondary" }}>
							{t("pbj_model.tracker.quarter_complete_note")}
						</Typography>
					)}
				</Stack>
			)}
		</>
	);
};
